import type { components } from "@connect/data/public/schema";
import { US_ONBOARDING } from "../config";

type Requirements = components["schemas"]["Requirements"];
type RequirementEntry = components["schemas"]["RequirementEntry"];
type RequirementFieldName = components["schemas"]["RequirementFieldName"];

const US_ONBOARDING_REQUIREMENTS: (keyof Requirements)[] = ["taxIdentifiers", "questionnaires"];

const COLLECTED_REQUIREMENTS = new Set<keyof Requirements>([
  "basicDetails",
  "residentialAddress",
  ...(US_ONBOARDING ? US_ONBOARDING_REQUIREMENTS : []),
]);

const SCREEN_REQUIREMENTS: Partial<Record<string, keyof Requirements>> = {
  "phone-number": "phoneNumber",
  "phone-otp": "phoneNumber",
  ssn: "taxIdentifiers",
  "due-diligence": "questionnaires",
  "enhanced-due-diligence": "questionnaires",
  "document-upload": "identityDocuments",
  "selfie-upload": "selfie",
  "proof-of-address": "proofOfAddress",
};

const NAME_FIELDS = new Set<RequirementFieldName>(["firstName", "lastName"]);
const DOB_FIELDS = new Set<RequirementFieldName>(["dateOfBirth", "nationality"]);
const SSN_FIELDS = new Set<RequirementFieldName>(["ssn"]);
const CDD_FIELDS = new Set<RequirementFieldName>(["customerDueDiligence"]);
const EDD_FIELDS = new Set<RequirementFieldName>(["enhancedDueDiligence"]);

function fieldsInclude(
  entry: RequirementEntry | undefined,
  wanted: Set<RequirementFieldName>,
): boolean {
  const fields = entry?.requiredFields;
  if (!fields) {
    return true;
  }
  return fields.some((field) => wanted.has(field));
}

export function isRequirementScreen(key: string): boolean {
  return key in SCREEN_REQUIREMENTS;
}

function isCollectedScreen(key: string): boolean {
  const requirement = SCREEN_REQUIREMENTS[key];

  return !requirement || COLLECTED_REQUIREMENTS.has(requirement);
}

export function shouldShowScreen(key: string, requirements: Requirements): boolean {
  if (!isCollectedScreen(key)) {
    return false;
  }

  switch (key) {
    case "verify-identity":
    case "processing":
    case "confirmation":
      return true;
    case "phone-number":
    case "phone-otp":
      return requirements.phoneNumber?.status === "incomplete";
    case "legal-name":
      return (
        requirements.basicDetails?.status === "incomplete" &&
        fieldsInclude(requirements.basicDetails, NAME_FIELDS)
      );
    case "dob-nationality":
      return (
        requirements.basicDetails?.status === "incomplete" &&
        fieldsInclude(requirements.basicDetails, DOB_FIELDS)
      );
    case "address-search":
    case "address-manual":
      return requirements.residentialAddress?.status === "incomplete";
    case "ssn":
      return (
        requirements.taxIdentifiers?.status === "incomplete" &&
        fieldsInclude(requirements.taxIdentifiers, SSN_FIELDS)
      );
    case "due-diligence":
      return (
        requirements.questionnaires?.status === "incomplete" &&
        fieldsInclude(requirements.questionnaires, CDD_FIELDS)
      );
    case "enhanced-due-diligence":
      return (
        requirements.questionnaires?.status === "incomplete" &&
        fieldsInclude(requirements.questionnaires, EDD_FIELDS)
      );
    case "document-upload":
      return requirements.identityDocuments?.status === "incomplete";
    case "selfie-upload":
      return requirements.selfie?.status === "incomplete";
    case "proof-of-address":
      return requirements.proofOfAddress?.status === "incomplete";
    default:
      return true;
  }
}

export function hasCollectionScreen(
  requirement: keyof Requirements,
  requirements: Requirements,
): boolean {
  if (!COLLECTED_REQUIREMENTS.has(requirement)) {
    return false;
  }

  const screenKeys = Object.keys(SCREEN_REQUIREMENTS).filter(
    (key) => SCREEN_REQUIREMENTS[key] === requirement,
  );

  return screenKeys.length === 0 || screenKeys.some((key) => shouldShowScreen(key, requirements));
}

export function filterScreens<T extends { key: string }>(
  screens: T[],
  requirements: Requirements | undefined,
): T[] {
  if (!requirements) {
    return screens;
  }
  return screens.filter((screen) => shouldShowScreen(screen.key, requirements));
}

/**
 * After a screen is completed, the filtered list may shrink and shift
 * later screens into the current index. Advance only when the completed
 * screen is still present (stale refetch); otherwise keep the index so the
 * next remaining screen is shown.
 */
export function indexAfterCompletingScreen<T extends { key: string }>(
  screens: T[],
  requirements: Requirements | undefined,
  completedKey: string,
  currentIndex: number,
): number {
  const nextScreens = filterScreens(screens, requirements);
  const lastIndex = Math.max(nextScreens.length - 1, 0);
  const stillPresent = nextScreens.some((screen) => screen.key === completedKey);
  if (stillPresent) {
    return Math.min(currentIndex + 1, lastIndex);
  }
  return Math.min(currentIndex, lastIndex);
}
